import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  CircularProgress
} from '@material-ui/core'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as CopyIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon
} from '@material-ui/icons'
import { Alert } from '@material-ui/lab'
import axios from 'axios'

const CalculationConfigList = ({ onEdit, onClose }) => {
  const [configs, setConfigs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [deleteDialog, setDeleteDialog] = useState({ open: false, configId: null })
  const [userPreference, setUserPreference] = useState(null)

  useEffect(() => {
    loadConfigs()
    loadUserPreference()
  }, [])

  const loadConfigs = async () => {
    try {
      setLoading(true)
      const response = await axios.get('/api/calculation-configs')
      setConfigs(response.data.configs || [])
      setError(null)
    } catch (err) {
      console.error('Error loading configs:', err)
      setError('Failed to load calculation configurations')
    } finally {
      setLoading(false)
    }
  }

  const loadUserPreference = async () => {
    try {
      const response = await axios.get('/api/calculation-configs/user/preference')
      setUserPreference(response.data.configId)
    } catch (err) {
      console.error('Error loading user preference:', err)
    }
  }

  const handleSetPreference = async (configId) => {
    try {
      await axios.put('/api/calculation-configs/user/preference', { configId })
      setUserPreference(configId)
    } catch (err) {
      console.error('Error setting preference:', err)
      setError('Failed to set preferred configuration')
    }
  }

  const handleDelete = async (configId) => {
    try {
      await axios.delete(`/api/calculation-configs/${configId}`)
      setConfigs(configs.filter(c => c._id !== configId))
      setDeleteDialog({ open: false, configId: null })
    } catch (err) {
      console.error('Error deleting config:', err)
      setError('Failed to delete configuration')
      setDeleteDialog({ open: false, configId: null })
    }
  }

  const handleClone = async (configId) => {
    try {
      const response = await axios.post(`/api/calculation-configs/${configId}/clone`)
      await loadConfigs()
    } catch (err) {
      console.error('Error cloning config:', err)
      setError('Failed to clone configuration')
    }
  }

  const handleCreate = () => {
    // Navigate to create screen with no ID
    onEdit(null)
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" style={{ minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Container maxWidth="lg" style={{ padding: '32px 0' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: 24 }}>
        <Typography variant="h4" component="h1">
          Calculation Configurations
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            style={{ marginRight: 16 }}
          >
            New Configuration
          </Button>
          {onClose && (
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" style={{ marginBottom: 24 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
        Manage calculation configurations for proposals. Each configuration contains all the rates,
        factors, and constants used in calculations. Set your preferred configuration with the star icon.
      </Typography>

      <Grid container spacing={3}>
        {configs.map((config) => (
          <Grid item xs={12} md={6} lg={4} key={config._id}>
            <Card
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                border: userPreference === config._id ? '2px solid #3f51b5' : 'none'
              }}
            >
              <CardContent style={{ flexGrow: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="h6" component="h2" gutterBottom>
                    {config.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleSetPreference(config._id)}
                    color={userPreference === config._id ? 'primary' : 'default'}
                    title={userPreference === config._id ? 'Your preferred config' : 'Set as preferred'}
                  >
                    {userPreference === config._id ? <StarIcon /> : <StarBorderIcon />}
                  </IconButton>
                </Box>

                <Typography variant="body2" color="textSecondary" paragraph>
                  {config.description || 'No description'}
                </Typography>

                <Box style={{ marginTop: 16 }}>
                  {config.isSystemDefault && (
                    <Chip label="System Default" size="small" color="primary" style={{ marginRight: 8, marginBottom: 8 }} />
                  )}
                  {config.entity && (
                    <Chip
                      label={`Entity: ${config.entity.name || config.entity}`}
                      size="small"
                      color="secondary"
                      style={{ marginRight: 8, marginBottom: 8 }}
                    />
                  )}
                  {!config.isActive && (
                    <Chip label="Inactive" size="small" color="default" style={{ marginRight: 8, marginBottom: 8 }} />
                  )}
                </Box>

                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 16 }}>
                  Created: {new Date(config.createdAt).toLocaleDateString()}
                </Typography>
              </CardContent>

              <CardActions>
                <IconButton
                  size="small"
                  onClick={() => onEdit(config._id)}
                  title="Edit configuration"
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleClone(config._id)}
                  title="Clone configuration"
                >
                  <CopyIcon />
                </IconButton>
                {!config.isSystemDefault && (
                  <IconButton
                    size="small"
                    onClick={() => setDeleteDialog({ open: true, configId: config._id })}
                    title="Delete configuration"
                    color="secondary"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {configs.length === 0 && (
        <Box textAlign="center" style={{ padding: '64px 0' }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No configurations found
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Create your first calculation configuration to get started
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Create Configuration
          </Button>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, configId: null })}
      >
        <DialogTitle>Delete Configuration?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this configuration? This action cannot be undone.
            Estimates that already use this configuration will not be affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, configId: null })}>
            Cancel
          </Button>
          <Button
            onClick={() => handleDelete(deleteDialog.configId)}
            color="secondary"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default CalculationConfigList
